import axios, { type AxiosResponse, type AxiosError } from 'axios';

import {
	getUserToken,
	getAdminToken,
	getUserRefreshToken,
	setUserToken,
	logOut
} from './authentication';

export type HttpResponseSuccess<T> = AxiosResponse<{
	code: 'OK' | 'ERROR';
	data: T;
	message: string;
}>;
export type HttpResponseError<T = undefined> = AxiosError<T>;

const service = (baseURL: string) => {
	if (!baseURL) {
		throw new Error('Invalid API url!');
	}

	const apiInstance = axios.create({
		baseURL
	});

	apiInstance.interceptors.request.use(config => {
		const token = getUserToken();
		const adminToken = getAdminToken();

		const newConfig = { ...config };

		newConfig.headers.Authorization = adminToken || token;
		newConfig.headers.TimeZone =
			Intl.DateTimeFormat().resolvedOptions().timeZone;

		return newConfig;
	});

	apiInstance.interceptors.response.use(
		response => response,
		async error => {
			const responseStatus = error.response.status;

			const originalRequest = error.config;
			let { isFetchingRefreshToken = false } = originalRequest;

			if (responseStatus === 401 && !isFetchingRefreshToken) {
				isFetchingRefreshToken = true;

				try {
					const refreshToken = getUserRefreshToken();

					const refreshTokenApiInstance = axios.create({
						baseURL,
						headers: {
							Authorization: refreshToken
						}
					});

					const response = await refreshTokenApiInstance.post(
						'/api/user/refreshToken'
					);

					const { accessToken } = response.data;

					setUserToken(accessToken);

					isFetchingRefreshToken = false;

					return apiInstance(originalRequest);
				} catch (newTokenError: any) {
					if (newTokenError.status === 401) {
						logOut();
						window.location.href = '/login';
					}

					return Promise.reject(error);
				}
			}

			return Promise.reject(error);
		}
	);

	return apiInstance;
};

export default service;
